import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Check, Download, RefreshCw, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { LetterEditor } from '../../components/LetterEditor';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { StatusBadge } from '../../components/StatusBadge';
import type { AppealSubmitDetails } from '../../lib/api';
import { api, ApiRequestError } from '../../lib/api';
import { friendlyError } from '../../lib/error_codes';

interface Citation {
  id: string;
  title: string;
  source: string;
  effective_date: string;
  excerpt: string;
  url?: string;
}

// Extracts [CITE:xxx] IDs from letter content and builds an ordered map.
function extractCitationIds(content: string): string[] {
  const matches = content.matchAll(/\[CITE:([^\]]+)\]/g);
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const m of matches) {
    const citId = m[1];
    if (citId !== undefined && !seen.has(citId)) {
      seen.add(citId);
      ordered.push(citId);
    }
  }
  return ordered;
}

function buildCitationMap(ids: string[]): Map<string, number> {
  return new Map(ids.map((id, i) => [id, i + 1]));
}

// Renders letter text, replacing [CITE:xxx] with superscript footnote numbers.
function renderLetter(
  content: string,
  citationMap: Map<string, number>,
  onCiteClick: (id: string) => void,
): React.ReactNode {
  const parts = content.split(/(\[CITE:[^\]]+\])/g);
  return parts.map((part, i) => {
    const match = part.match(/^\[CITE:([^\]]+)\]$/);
    if (match) {
      const id = match[1];
      if (!id) return <span key={i}>{part}</span>;
      const idx = citationMap.get(id);
      return (
        <button
          key={`cite-${i}`}
          type="button"
          onClick={() => onCiteClick(id)}
          aria-label={`View citation ${idx}`}
          className="mx-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded bg-primary-100 px-1 text-[10px] font-bold text-primary-700 hover:bg-primary-200"
        >
          {idx}
        </button>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

interface SubmitModalProps {
  onConfirm: (details: AppealSubmitDetails) => void;
  onCancel: () => void;
  isPending: boolean;
}

function SubmitModal({ onConfirm, onCancel, isPending }: SubmitModalProps) {
  const [method, setMethod] = useState<AppealSubmitDetails['submission_method']>('portal');
  const [tracking, setTracking] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        <h3 className="mb-4 text-base font-semibold text-gray-900">Mark as Submitted</h3>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Submission Method
            </label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as AppealSubmitDetails['submission_method'])}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-primary-600 focus:outline-none"
            >
              <option value="portal">Payer Portal</option>
              <option value="fax">Fax</option>
              <option value="mail">Mail</option>
              <option value="clearinghouse">Clearinghouse</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Tracking Number (optional)
            </label>
            <input
              type="text"
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              placeholder="e.g., confirmation ID from portal"
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-primary-600 focus:outline-none"
            />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isPending}
            onClick={() =>
              onConfirm({
                submission_method: method,
                submission_tracking_number: tracking || undefined,
              })
            }
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {isPending ? 'Submitting…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AppealReview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const citationPanelRef = useRef<HTMLDivElement>(null);

  const [editedContent, setEditedContent] = useState<string | null>(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [activeCitationId, setActiveCitationId] = useState<string | null>(null);
  const [streamedTokens, setStreamedTokens] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  const { data: appeal, isLoading } = useQuery({
    queryKey: ['appeal', id],
    queryFn: () => api.appeals.get(id!),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'generating') return 3000;
      return false;
    },
  });

  // Start SSE streaming when appeal is generating.
  useEffect(() => {
    if (appeal?.status !== 'generating' || isStreaming) return;

    const es = new EventSource(`/v1/appeals/${id}/stream`, { withCredentials: true });
    eventSourceRef.current = es;
    setIsStreaming(true);
    setStreamedTokens('');

    es.addEventListener('token', (e) => {
      try {
        const parsed = JSON.parse(e.data) as { text: string };
        setStreamedTokens((t) => t + parsed.text);
      } catch {
        // ignore malformed token events
      }
    });

    es.addEventListener('complete', () => {
      es.close();
      eventSourceRef.current = null;
      setIsStreaming(false);
      qc.invalidateQueries({ queryKey: ['appeal', id] });
    });

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
      setIsStreaming(false);
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
      setIsStreaming(false);
    };
  }, [appeal?.status, id, isStreaming, qc]);

  const saveMutation = useMutation({
    mutationFn: (content: string) => api.appeals.patch(id!, { letter_content: content }),
    onSuccess: () => {
      toast.success('Letter saved.');
      qc.invalidateQueries({ queryKey: ['appeal', id] });
    },
    onError: (err) => {
      toast.error(friendlyError(err instanceof ApiRequestError ? err.code : 'UNKNOWN_ERROR'));
    },
  });

  const approveMutation = useMutation({
    mutationFn: () => api.appeals.approve(id!),
    onSuccess: () => {
      toast.success('Appeal approved and ready to submit.');
      qc.invalidateQueries({ queryKey: ['appeal', id] });
    },
    onError: (err) => {
      toast.error(friendlyError(err instanceof ApiRequestError ? err.code : 'UNKNOWN_ERROR'));
    },
  });

  const submitMutation = useMutation({
    mutationFn: (details: AppealSubmitDetails) => api.appeals.submit(id!, details),
    onSuccess: () => {
      setShowSubmitModal(false);
      toast.success('Appeal marked as submitted.');
      qc.invalidateQueries({ queryKey: ['appeal', id] });
    },
    onError: (err) => {
      toast.error(friendlyError(err instanceof ApiRequestError ? err.code : 'UNKNOWN_ERROR'));
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: () => api.appeals.regenerate(id!),
    onSuccess: () => {
      toast.info('Regeneration started.');
      setEditedContent(null);
      setStreamedTokens('');
      qc.invalidateQueries({ queryKey: ['appeal', id] });
    },
    onError: (err) => {
      toast.error(friendlyError(err instanceof ApiRequestError ? err.code : 'UNKNOWN_ERROR'));
    },
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <LoadingSkeleton rows={10} />
      </div>
    );
  }

  if (!appeal) return null;

  const letterContent = editedContent ?? appeal.letter_content ?? '';
  const displayContent =
    appeal.status === 'generating' && streamedTokens ? streamedTokens : letterContent;

  const citationIds = extractCitationIds(displayContent);
  const citationMap = buildCitationMap(citationIds);

  // Build citation objects from `citations_used` array (IDs) - in a real app
  // these would be fetched from the API. We render the IDs as placeholders.
  const citations: Citation[] = citationIds.map((id, idx) => ({
    id,
    title: `Citation ${idx + 1}`,
    source: id,
    effective_date: '',
    excerpt: 'Full citation detail available in the source database.',
  }));

  const hasCitationFailure = appeal.citation_verification_status === 'failed';
  const canEdit =
    appeal.status === 'ready_for_review' || appeal.status === 'approved';
  const canApprove = appeal.status === 'ready_for_review';
  const canSubmit = appeal.status === 'approved' && !hasCitationFailure;

  function scrollToCitation(citId: string) {
    setActiveCitationId(citId);
    const el = citationPanelRef.current?.querySelector(`[data-citation-id="${citId}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  return (
    <ErrorBoundary>
      {showSubmitModal && (
        <SubmitModal
          onConfirm={(details) => submitMutation.mutate(details)}
          onCancel={() => setShowSubmitModal(false)}
          isPending={submitMutation.isPending}
        />
      )}

      <div className="flex h-full flex-col gap-4">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <button
              type="button"
              onClick={() => navigate('/appeals')}
              className="mb-1 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to appeals
            </button>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-semibold text-gray-900">Appeal Review</h1>
              <StatusBadge status={appeal.status} />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => regenerateMutation.mutate()}
              disabled={regenerateMutation.isPending || appeal.status === 'generating'}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Regenerate
            </button>

            {canEdit && editedContent !== null && (
              <button
                type="button"
                onClick={() => saveMutation.mutate(editedContent)}
                disabled={saveMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                {saveMutation.isPending ? 'Saving…' : 'Save Edits'}
              </button>
            )}

            <a
              href={`/v1/appeals/${id}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              <Download className="h-3.5 w-3.5" />
              Download PDF
            </a>

            {canApprove && (
              <button
                type="button"
                onClick={() => approveMutation.mutate()}
                disabled={approveMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60"
              >
                <Check className="h-3.5 w-3.5" />
                {approveMutation.isPending ? 'Approving…' : 'Approve'}
              </button>
            )}

            {canSubmit && (
              <button
                type="button"
                onClick={() => setShowSubmitModal(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
              >
                <Send className="h-3.5 w-3.5" />
                Mark as Submitted
              </button>
            )}
          </div>
        </div>

        {/* Citation verification failure banner */}
        {hasCitationFailure && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-red-800">Citation verification failed</p>
              {appeal.citation_verification_errors && appeal.citation_verification_errors.length > 0 && (
                <ul className="mt-1 list-inside list-disc text-red-700">
                  {appeal.citation_verification_errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              )}
              <p className="mt-1 text-red-600">
                Submission is disabled until citation issues are resolved.
              </p>
            </div>
          </div>
        )}

        {/* Split view: letter + citations */}
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Left: letter editor or preview */}
          <div className="flex min-h-[320px] flex-col rounded-lg border border-gray-200 bg-white lg:min-h-0">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2">
              <span className="text-xs font-medium text-gray-600">
                Appeal Letter
                {appeal.status === 'generating' && (
                  <span className="ml-2 inline-flex items-center gap-1 text-amber-600">
                    <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
                    Generating…
                  </span>
                )}
              </span>
              {canEdit && (
                <span className="text-[11px] text-gray-400">
                  Click in the letter to edit
                </span>
              )}
            </div>

            {canEdit ? (
              <LetterEditor
                value={displayContent}
                onChange={setEditedContent}
                className="flex-1"
              />
            ) : (
              <div className="flex-1 overflow-y-auto p-4">
                <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed text-gray-800">
                  {renderLetter(displayContent, citationMap, scrollToCitation)}
                </pre>
              </div>
            )}
          </div>

          {/* Right: citations panel */}
          <div
            ref={citationPanelRef}
            className="min-h-[240px] overflow-y-auto rounded-lg border border-gray-200 bg-white lg:min-h-0"
          >
            <div className="border-b border-gray-100 px-4 py-2">
              <span className="text-xs font-medium text-gray-600">
                Citations ({citations.length})
              </span>
            </div>

            {citations.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">
                No citations found in this letter.
              </div>
            ) : (
              <ol className="divide-y divide-gray-100">
                {citations.map((citation, idx) => (
                  <li
                    key={citation.id}
                    data-citation-id={citation.id}
                    className={`p-4 transition-colors ${
                      activeCitationId === citation.id ? 'bg-primary-50' : ''
                    }`}
                  >
                    <div className="mb-1 flex items-start gap-2">
                      <span className="flex-shrink-0 rounded bg-primary-100 px-1.5 py-0.5 text-[10px] font-bold text-primary-700">
                        {idx + 1}
                      </span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{citation.title}</p>
                        {citation.source && (
                          <p className="text-xs text-gray-500">{citation.source}</p>
                        )}
                        {citation.effective_date && (
                          <p className="text-xs text-gray-400">
                            Effective: {citation.effective_date}
                          </p>
                        )}
                      </div>
                    </div>
                    {citation.excerpt && (
                      <blockquote className="ml-6 mt-2 border-l-2 border-gray-200 pl-3 text-xs leading-relaxed text-gray-600">
                        {citation.excerpt}
                      </blockquote>
                    )}
                    {citation.url && (
                      <a
                        href={citation.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-6 mt-1 block text-xs text-primary-600 hover:underline"
                      >
                        View source
                      </a>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}

// Appeals list page — simple index of appeals with status + navigation.
export function AppealsList() {
  const navigate = useNavigate();
  const { data: appeals, isLoading } = useQuery({
    queryKey: ['appeals'],
    queryFn: () => api.appeals.list(),
  });

  if (isLoading) return <div className="p-6"><LoadingSkeleton /></div>;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-gray-900">Appeals</h1>
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        {!appeals || appeals.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-400">
            No appeals yet. Generate one from a denial detail page.
          </div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full min-w-[480px] text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left">
                <th className="px-4 py-3 font-medium text-gray-600">Appeal ID</th>
                <th className="px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 font-medium text-gray-600">Level</th>
                <th className="px-4 py-3 font-medium text-gray-600">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {appeals.map((appeal) => (
                <tr
                  key={appeal.id}
                  onClick={() => navigate(`/appeals/${appeal.id}`)}
                  className="cursor-pointer hover:bg-gray-50"
                >
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{appeal.id}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={appeal.status} />
                  </td>
                  <td className="px-4 py-3 text-gray-600">Level {appeal.appeal_level}</td>
                  <td className="px-4 py-3 text-gray-500">
                    {new Date(appeal.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
